import { Box, MenuItem, Stack, useTheme } from "@mui/material";
import { Ref, forwardRef, useEffect, useImperativeHandle, useMemo} from "react";
import { useTranslation } from "react-i18next";
import { useReportTaskList } from "../../queries/scheduled-reports/UseReportTaskList";
import { ScheduledReportsListItem } from "./ScheduledReportsListItem";
import ScheduledReportsListUtils, { ScheduledReportsActionEnum, ScheduledReportsItem, reportTypes } from "./ScheduledReportsListUtils";
import useScheduledReportsListState from "./ScheduledReportsListState";
import { useOrganizations } from "../../queries/organizations/UseOrganizations";
import { ReportTypeEnum } from "../../services/reporting-data-service/ReportScheduleModels";
import ScheduledReportsEditorDialog, { bindScheduledReportsEditorDialog, useScheduledReportsEditorDialogState } from "../scheduled-reports-editor/ScheduledReportsEditorDialog";
import { useDeleteScheduledReport } from "../../queries/scheduled-reports/UseDeleteScheduledReport";
import _ from "lodash";
import ScheduledReportRecipientDialog, { bindScheduledReportRecipientDialog, useScheduledReportRecipientDialogState } from "../scheduled-reports-recipient-dialog/ScheduledReportRecipientDialog";
import { useUserContext } from "../../contexts/user-context/UserContext";
import { useConfirmation } from "../../contexts/confirmation-context/ConfirmationContext";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import { BaseActions, BaseActionEnum } from "../action-menu/ActionMenuItem";
import { ContentContainer } from "../content-container/ContentContainer";
import { ContentSection } from "../content-section/ContentSection";
import { ViewOptionStateEnum } from "../contextual-app-bar/ViewOptionStateEnum";
import LoadingZone from "../loading-zone/LoadingZone";
import NoData from "../no-data/NoData";
import OptionsSideSheet from "../options-side-sheet/OptionsSideSheet";
import SideSheetFilterInput from "../side-sheet/side-sheet-filter-input/SideSheetFilterInput";
import SideSheetSelect from "../side-sheet/side-sheet-select/SideSheetSelect";
import VirtualizedList from "../virtualized-list/VirtualizedList";
import { contains } from "../../common/utilities/Contains";
import { AppNotificationTypeEnum } from "../../contexts/app-notification-context/AppNotificationContextTypes";
import { useAppNotificationContext } from "../../contexts/app-notification-context/UseAppNotificationContext";
import { RestError } from "../../services/rest-service/RestService";
import ReportingDataService from "../../services/reporting-data-service/ReportingDataService";
import { DeliveryOverrideType, IScheduledReportOptions } from "../../services/reporting-data-service/Models";

export type ScheduledReportsListType = {
    executeAction: (action: ScheduledReportsActionEnum | BaseActions) => void;
};

export type ScheduledReportsListProps = {
    groupID: number;
    organizationID: number;
    myReports: boolean;
    setViewOptionState?: (state: ViewOptionStateEnum) => void;

};

function ScheduledReportsList({
    groupID,
    organizationID,
    myReports,
    setViewOptionState,
}: ScheduledReportsListProps, ref: Ref<unknown> | undefined) {

    const [userState] = useUserContext();
    const { t } = useTranslation();
    const { confirm, acknowledge } = useConfirmation();
    const [, setAppNotificationState] = useAppNotificationContext(); 
    const theme = useTheme();
    const scheduledReportsEditorDialog = useScheduledReportsEditorDialogState();
    const recipientDialog = useScheduledReportRecipientDialogState();

    const {
        state,
        setFilterText,
        setScheduledReportType,
        resetState,
    } = useScheduledReportsListState();

    
    const {data, status, refetch } = useQueryNotifications(useReportTaskList)({
        organizationID: organizationID,        
        groupID: groupID,
        myReports: myReports
    });

    const {data: organizations} = useQueryNotifications(useOrganizations)({
    });

    const organization = organizations?.find(o => o.id === organizationID);

    const scheduledReports = useMemo(() => {
        return ScheduledReportsListUtils.shapeData(data ?? [], state.filterText, state.scheduledReportType, userState.user?.isElynxAdmin ?? false);
    }, [
        data,
        state.filterText,
        state.scheduledReportType,
        userState.user
    ]);

    //console.log(`${scheduledReports.flatMap(o => o.notifications).map(o => o.displayText)}`);

    const { mutate: deleteScheduledReport } = useQueryNotifications(useDeleteScheduledReport)({
        organizationID: organizationID,
        groupID: groupID,
        onSuccess: () => {
            acknowledge(t("deleteSuccessMessage", { itemName: t("dataTypeNames.scheduledReport") }));
        }
    });

    const nodata = status === "success" && (scheduledReports?.length ?? 0) === 0; 

    useImperativeHandle<unknown, ScheduledReportsListType>(
        ref,
        () => ({
             executeAction: (action) => {onListAction(action)} 
        })
    );

    async function _edit(id: number, copy: boolean) {
        const result = await scheduledReportsEditorDialog.open({
            organizationID,
            groupID,
            taskID: id,
            myReports: myReports,
            copy: copy
        });
        
        if (result.reason === 'save') {
            await refetch();
        }
    }

    async function onAdd() {
        _edit(-1, false);
    } 

    async function onEdit(item: ScheduledReportsItem) {

        if (item.canCopy && item.canDelete) {
            _edit(item.id, false);
        } else {
            if (item.emailNotifications.length === 0) {

                await confirm({
                    dialogTitle: t("scheduledReports.notificationListOnlyConfirmation.title"),
                    confirmationText: t("scheduledReports.notificationListOnlyConfirmation.confirmationText"),
                    yesButtonText: t("buttons.okay"),
                    showConfirmationText: true,
                    showYesButton: true,
                    showNoButton: false,
                    showCloseButton: false
                });

            } else {

                const recipients = _.cloneDeep(item.emailNotifications);

                recipients.forEach(o => o.selected = true);

                await recipientDialog.open({
                    groupID: groupID,
                    taskID: item.id,
                    recipients: recipients
                });
            }
        }
    }

    function onCopy(item: ScheduledReportsItem) {
        _edit(item.id, true);
    }

    async function onDelete(item: ScheduledReportsItem) {
        const result = await confirm({
            dialogTitle: t("deleteConfirmation.title", { itemName: t("dataTypeNames.scheduledReport") }),
            confirmationText: t("deleteConfirmation.confirmationText"),
            yesButtonText: t("deleteConfirmation.yesButtonText"),
            noButtonText: t("deleteConfirmation.noButtonText"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true,
            showCloseButton: true
        });
         
        if ( result === "yes") {
            deleteScheduledReport(item.id);
        }
    }

    async function onRun(item: ScheduledReportsItem) {

        try {

            const reportOptions: IScheduledReportOptions =
            {
                overrideReportDates: false,
                startDateOverride: undefined,
                endDateOverride: undefined,
                customerID: userState.customer!.id,
                taskIDs: [],
           
                includeUnscheduledTasks: true,
                deliveryOverrideType: DeliveryOverrideType.Default,
                emailOverrides: [],
                //ftpOverride: string;
                deleteReportAfterDelivery: true,
                disableDBUpdate: false,
            
                //isTestMessage: boolean;
            
                //runKey: string;
                logPrefix: 'AppAPI: ',
                //outputFormat: string;
            
                //groupOverrideID?: number;
                //tatOverrideID?: number;
                includeSubgroupsWithOverride: false,
                //languageOverride?: UILanguage;
                //timeZoneOverride?: number;
                //templatePath: string;
                //emailBody: string; 
                //emailSubject: string;
                useSingleSchedule: true
            };

            await ReportingDataService.runScheduledReport(item.id, reportOptions);

            acknowledge(`${item.reportName} has been scheduled to run`)

        } catch(error) {
            const restError = error as RestError;
            setAppNotificationState({
                notificationType: AppNotificationTypeEnum.Error,
                show: true,
                error: restError
            });
        }
    }

    function onListAction(action: ScheduledReportsActionEnum | BaseActions, payload?: ScheduledReportsItem) {

        switch (action) {
            case BaseActionEnum.CreateNew:
                onAdd();
                break;

            case BaseActionEnum.Edit:
                onEdit(payload as ScheduledReportsItem);
                break;

            case BaseActionEnum.Copy:
                onCopy(payload as ScheduledReportsItem);
                break;

            case BaseActionEnum.Delete:
                 onDelete(payload as ScheduledReportsItem);
                 break;

            case BaseActionEnum.Refresh:
                refetch();
                break;

            case BaseActionEnum.Run: 
                onRun(payload as ScheduledReportsItem);
            break;
        }
    }

    function renderItem(_index: number, item: ScheduledReportsItem) {
        return (
            <ScheduledReportsListItem
                key={`${item.id}`}
                item={item}
                onAction={onListAction}
            />
        );
    }

    function onFilterItem(item: ScheduledReportsItem): boolean {

        return !!contains(item.name, state.filterText);

    }

     useEffect(() => {
        setViewOptionState && setViewOptionState(state.viewOptionState);
    }, [
        state.viewOptionState,
        setViewOptionState
    ]);

    return (
        <LoadingZone isLoading={status === 'loading'}>
            <ContentContainer>
                <ContentSection>
                    {nodata ? 
                        <NoData
                            title={t("scheduledReports.nodata.title")}
                            text={<Stack
                                direction="column"
                                spacing={0.5}
                                >
                                <Box>
                                    {myReports ? 
                                        t("scheduledReports.nodata.myreports") :
                                    state.scheduledReportType === ReportTypeEnum.None ?
                                    t("scheduledReports.nodata.textall", {
                                        org: organization?.displayText
                                    }) :
                                    t("scheduledReports.nodata.text", {
                                        reportType: ReportTypeEnum[state.scheduledReportType], 
                                        org: organization?.displayText
                                     })
                                    }
                                </Box>
                                <Box sx={{
                                    paddingTop: theme.spacing(2)
                                }}>
                                    {t("scheduledReports.nodata.instruction")}
                                </Box>
                            </Stack>
                            }
                        /> :
                        null
                    }
                
                    <VirtualizedList
                        items={scheduledReports ?? []} 
                        renderItem={renderItem}
                        filter={onFilterItem}
                    />
                
                </ContentSection>

                <OptionsSideSheet 
                    onReset={resetState}
                >

                    <SideSheetSelect
                        label={t('scheduledReports.reportType')}
                        value={state.scheduledReportType.toString()}
                        onChange={(value: string) => {
                            setScheduledReportType(+value);
                    }}>
                        {reportTypes.map((o, index) => {
                            return (
                                <MenuItem key={index} value={o.value}>
                                    {o.text}
                                </MenuItem>
                            );
                        })}
                    </SideSheetSelect> 


                    
                    <SideSheetFilterInput
                        label={t("scheduledReports.filterBy")}
                        value={state.filterText}
                        onChange={(value: string) => {
                            setFilterText(value);
                        }}
                    />


                </OptionsSideSheet>
                
            </ContentContainer>
            <ScheduledReportsEditorDialog {...bindScheduledReportsEditorDialog(scheduledReportsEditorDialog)} />
            <ScheduledReportRecipientDialog {...bindScheduledReportRecipientDialog(recipientDialog)} />
        </LoadingZone>
    );
}

export default forwardRef(ScheduledReportsList);